/**
 * GroundPlane — R3F component that renders a large ground plane.
 *
 * Four variants controlled by `groundVariant`:
 *  - 'concrete'     PBR ground with diffuse + normal textures (default)
 *  - 'reflective'   Dark MeshReflectorMaterial plane (clean, no PBR textures)
 *  - 'road'         Alpha-textured road markings (transparent except white lines)
 *  - fallback       Flat colour when no textures provided
 *
 * The reflective variant uses proven values from the Drei reference:
 * https://drei.docs.pmnd.rs/shaders/mesh-reflector-material
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import { Suspense, useEffect } from 'react';
import { useTexture, MeshReflectorMaterial } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import type { GroundVariant } from '../api/types';

// Reusable constant — avoids creating a new Vector2 on every render.
const NORMAL_SCALE = new THREE.Vector2(1, 1);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GroundPlaneProps {
  /** Ground type — 'pbr' loads textures, 'color' uses flat colour. Default: 'color' */
  type?: 'color' | 'pbr' | 'alpha-texture';
  /** Tint/base color (hex). In PBR mode multiplies the diffuse texture. Default: '#ffffff' */
  color?: string;
  /** Diffuse texture URL. Only used when type='pbr'. */
  textureUrl?: string;
  /** Normal map URL (GL convention). Only used when type='pbr'. */
  normalUrl?: string;
  /** UV repeat count — how many times the texture tiles across the diameter. Default: 1 */
  repeat?: number;
  /** Plane diameter. Default: 50 */
  size?: number;
  /** Ground surface variant. Default: 'concrete' */
  groundVariant?: GroundVariant;
  /** Reflection intensity (0–1). Only used when groundVariant='reflective'. */
  groundReflectivity?: number;
  /** Mirror road texture for RHD markets. Only used when groundVariant='road'. */
  roadMirror?: boolean;
}

// ---------------------------------------------------------------------------
// Concrete ground — PBR material with diffuse + normal textures
// ---------------------------------------------------------------------------

function ConcreteGroundPlane({ color = '#ffffff', textureUrl, normalUrl, repeat = 1, size = 50 }: GroundPlaneProps) {
  const maps = useTexture({
    ...(textureUrl ? { map: textureUrl } : {}),
    ...(normalUrl ? { normalMap: normalUrl } : {}),
  });

  const applyRepeat = (tex: THREE.Texture) => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeat, repeat);
    tex.needsUpdate = true;
  };
  if (maps.map) applyRepeat(maps.map);
  if (maps.normalMap) applyRepeat(maps.normalMap);

  return (
    <mesh position={[0, -0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-500}>
      <circleGeometry args={[size / 2, 64]} />
      <meshStandardMaterial
        color={color}
        map={maps.map}
        normalMap={maps.normalMap}
        normalScale={NORMAL_SCALE}
        roughness={0.5}
        metalness={0.5}
        side={THREE.FrontSide}
        depthWrite
      />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Reflective ground — MeshReflectorMaterial (no PBR textures, clean plane)
//
// Values taken directly from the verified test scene / Drei reference.
// mixStrength is scaled by groundReflectivity (0–1 → 0–15).
// ---------------------------------------------------------------------------

function ReflectorGroundPlane({ size = 50, groundReflectivity = 0.5 }: GroundPlaneProps) {
  return (
    <mesh position={[0, -0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-500} receiveShadow>
      <planeGeometry args={[size, size]} />
      <MeshReflectorMaterial
        blur={[1, 5]}
        resolution={1024}
        mixBlur={0.85}
        mixStrength={groundReflectivity * 50}
        mixContrast={0.75}
        mirror={0}
        depthScale={0.5}
        minDepthThreshold={0.9}
        maxDepthThreshold={1}
        depthToBlurRatioBias={0.05}
        reflectorOffset={0}
        color="#101010"
        metalness={0.9}
        roughness={0.75}
      />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Road ground — alpha-textured road markings (transparent background)
//
// Renders white lane markings from roadTexture.png with full transparency
// for the rest. Positioned slightly below shadows so markings + shadows
// coexist visually. `roadMirror` flips the Z axis (driving direction) for RHD markets.
// ---------------------------------------------------------------------------

function RoadGroundPlane({ textureUrl, size = 50, roadMirror = false }: GroundPlaneProps) {
  const { gl } = useThree();
  const roadMap = useTexture(textureUrl ?? '/assets/textures/roadTexture.png');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- Three.js texture from useTexture requires direct mutation of wrap/filter properties
    roadMap.wrapS = roadMap.wrapT = THREE.RepeatWrapping;
    roadMap.repeat.set(1, 1);
    // Anisotropic filtering — eliminates blockiness at oblique viewing angles.
    // In useEffect so it triggers a single GPU re-upload after mount.
    roadMap.anisotropy = gl.capabilities.getMaxAnisotropy();
    roadMap.minFilter = THREE.LinearMipMapLinearFilter;
    roadMap.magFilter = THREE.LinearFilter;
    roadMap.needsUpdate = true;
  }, [roadMap, gl]);

  // Mirror: flip Z axis (driving direction) for RHD markets.
  // The plane is rotated -90° around X, so local Y maps to world Z.
  const zFlip = roadMirror ? -1 : 1;

  return (
    <mesh
      position={[0, -0.002, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      scale={[1, zFlip, 1]}
      renderOrder={-510}
    >
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial
        map={roadMap}
        transparent
        // Very low alphaTest: cuts fully-transparent background pixels (prevents
        // the rectangular plane boundary showing as a faint grey box), while
        // still passing the designed alpha gradient at marking edges.
        alphaTest={0.001}
        depthWrite={false}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Public component — dispatches to the correct variant
// ---------------------------------------------------------------------------

export function GroundPlane({
  type = 'color', color = '#cccccc', textureUrl, normalUrl,
  repeat = 1, size = 50, groundVariant = 'concrete', groundReflectivity = 0,
  roadMirror = false,
}: GroundPlaneProps) {

  // Variant: road — alpha-textured road markings
  if (groundVariant === 'road') {
    return (
      <Suspense fallback={null}>
        <RoadGroundPlane textureUrl={textureUrl} size={size} roadMirror={roadMirror} />
      </Suspense>
    );
  }

  // Variant: reflective — clean MeshReflectorMaterial plane
  if (groundVariant === 'reflective') {
    return (
      <Suspense fallback={null}>
        <ReflectorGroundPlane size={size} groundReflectivity={groundReflectivity} />
      </Suspense>
    );
  }

  // Variant: concrete — PBR with textures
  if (type === 'pbr' && (textureUrl || normalUrl)) {
    return (
      <Suspense fallback={null}>
        <ConcreteGroundPlane
          color={color} textureUrl={textureUrl} normalUrl={normalUrl}
          repeat={repeat} size={size}
        />
      </Suspense>
    );
  }

  // Fallback: flat colour
  return (
    <mesh position={[0, -0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-500}>
      <circleGeometry args={[size / 2, 64]} />
      <meshStandardMaterial color={color} roughness={0.95} metalness={0} side={THREE.FrontSide} depthWrite />
    </mesh>
  );
}
